import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocale } from "@/i18n/LocaleProvider";
import { useAuth } from "@/auth/AuthProvider";
import { canUseAgentPanel } from "@/auth/landing";
import { displayName, firstName, initials, planBadgeLabel, planKey } from "@/lib/identity";
import { ChevronDown } from "@/v3/Icons";

/* The header identity chip, and the menu it opens.
 *
 * A LIST, not two hardcoded rows. The messaging widget's inbox link is
 * already spoken for here, so the items are data and the rendering does not
 * care how many there are — adding one is a line in the array.
 *
 * The chip used to navigate straight to the panel. It opens the menu now, and
 * "My account" inside the menu is what navigates: a chip that both shows
 * identity and hides the only way to sign out was the defect.
 *
 * Sign-out stays in the panel sidebar too. Two exits, both real — someone
 * already inside the panel should not have to come back out to leave.
 */

type Item =
  | { kind: "link"; id: string; label: string; to: string }
  | { kind: "action"; id: string; label: string; run: () => void; danger?: true }
  | { kind: "divider"; id: string };

export function UserMenu() {
  const { t, locale } = useLocale();
  const { profile, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLElement | null>>([]);

  const close = useCallback(() => { setOpen(false); setActive(0); }, []);

  /* Outside-click and Escape. Both close it; neither navigates. */
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { close(); } };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  if (!profile) return null;

  const name = displayName(profile.name, profile.email, t.panel.memberFallback);
  const panelPath = canUseAgentPanel(profile) ? "/agent" : "/account";

  const items: Item[] = [
    { kind: "link", id: "account", label: t.panel.myAccount, to: panelPath },
    { kind: "divider", id: "d1" },
    {
      kind: "action", id: "signout", label: t.panel.logout, danger: true,
      // Full teardown, then home: leaving a signed-out visitor on a member
      // page would bounce them to /login and look like a failure.
      run: () => { close(); signOut(); navigate("/"); },
    },
  ];
  const focusable = items.filter((i) => i.kind !== "divider");

  function move(delta: number) {
    const next = (active + delta + focusable.length) % focusable.length;
    setActive(next);
    itemRefs.current[next]?.focus();
  }

  function onMenuKey(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") { e.preventDefault(); move(1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); move(-1); }
    else if (e.key === "Home") { e.preventDefault(); setActive(0); itemRefs.current[0]?.focus(); }
    else if (e.key === "End") {
      e.preventDefault();
      const last = focusable.length - 1;
      setActive(last); itemRefs.current[last]?.focus();
    }
  }

  let focusIndex = -1;

  return (
    <div className="sq-usermenu" ref={wrapRef}>
      <button type="button" className="sq-userchip"
        aria-haspopup="menu" aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" && !open) { e.preventDefault(); setOpen(true); }
        }}>
        <span className="sq-userchip__avatar" aria-hidden="true">
          {initials(name, t.panel.memberFallback)}
        </span>
        <span className="sq-userchip__name">
          {firstName(name, t.panel.memberFallback) || t.panel.myAccount}
        </span>
        {/* The plan badge stays in the chip at every width the chip keeps its
            text — it is identity, not decoration. */}
        <span className={`sq-userchip__plan sq-userchip__plan--${planKey(profile.plan)}`}>
          {planBadgeLabel(profile.plan, locale)}
        </span>
        {/* THE DESIGN'S CHEVRON, not a text character. This was «▾» (U+25BE)
            at 9px in Noto Sans Arabic — an 8×19 box, which reads as a stray
            dot beside the plan badge rather than as a caret. The market
            switcher already uses this icon; the chip now agrees with it. */}
        <span className="sq-userchip__caret" aria-hidden="true"><ChevronDown /></span>
      </button>

      {open && (
        // Anchored with logical inset, so it hangs from the chip's inner edge
        // in both directions rather than flying off the side in Arabic.
        <ul className="sq-usermenu__list" role="menu" onKeyDown={onMenuKey}>
          {items.map((item) => {
            if (item.kind === "divider") {
              return <li key={item.id} className="sq-usermenu__div" role="separator" />;
            }
            focusIndex += 1;
            const i = focusIndex;
            const ref = (el: HTMLElement | null) => { itemRefs.current[i] = el; };
            const cls = `sq-usermenu__item${item.kind === "action" && item.danger ? " sq-usermenu__item--danger" : ""}`;
            return (
              <li key={item.id} role="none">
                {item.kind === "link" ? (
                  <button type="button" role="menuitem" className={cls}
                    ref={ref as React.Ref<HTMLButtonElement>}
                    tabIndex={i === active ? 0 : -1}
                    onClick={() => { close(); navigate(item.to); }}>
                    {item.label}
                  </button>
                ) : (
                  <button type="button" role="menuitem" className={cls}
                    ref={ref as React.Ref<HTMLButtonElement>}
                    tabIndex={i === active ? 0 : -1}
                    onClick={item.run}>
                    {item.label}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
